import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardQuery } from '../../hooks/queries/useDashboardQuery';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import { StoreInfo } from '../../types/dashboard';
import './StoreInfoCard.css';

const StoreInfoCard: React.FC = () => {
  const { t } = useTranslation();
  const { 
    dateRange, 
    dayOfWeek, 
    time, 
    diningBehavior, 
    isLoadingDefaultDates,
    isFiltersApplying 
  } = useFilters();
  
  
  const getSalesforceIdFromURL = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('storeId');
  };
  
  const salesforceId = getSalesforceIdFromURL();
  
  
  const { 
    data: dashboardData, 
    isLoading: loading, 
    error: queryError 
  } = useDashboardQuery({
    storeId: salesforceId || '',
    filters: {
      dateRange,
      dayOfWeek,
      time,
      diningBehavior
    },
    enabled: !isLoadingDefaultDates && !!salesforceId
  });
  
  const error = queryError ? 
    (queryError instanceof Error ? queryError.message : 'An error occurred while fetching data') : 
    null;
  const { isLaptop, isiPad, isiPhone } = useResponsive();

  const formatNumber = (num: number | string | undefined | null): string => {
    if (num === undefined || num === null) return t('storeInfo.notAvailable');
    
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return t('storeInfo.notAvailable');
    
    return numValue.toLocaleString();
  };

  const storeInfo: StoreInfo | null = dashboardData?.storeInfo || null;

  if (loading || isFiltersApplying) {
    return (
      <div className="store-info-card">
        <div className="store-info-loading">
          <div className="store-info-spinner"></div>
          <div>{t('storeInfo.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !storeInfo) {
    return (
      <div className="store-info-card">
        <div className="store-info-error">
          <div className="error-icon">⚠️</div>
          <div>{error || t('storeInfo.errorNotFound')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="store-info-card">
      {isFiltersApplying && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span>{t('storeInfo.filterApplying')}</span>
        </div>
      )}
      
      <div className="store-name-section">
        <div className="store-name-text">
          <h2>{storeInfo.Store_name}</h2>
        </div>
      </div>

      <div className="basic-info-grid">
        <div className="info-card">
          <div className="info-left">
            <div className="info-icon">🏷️</div>
            <div className="info-label">{t('storeInfo.storeType')}</div>
          </div>
          <div className="info-value">{storeInfo.StoreType}</div>
        </div>
        
        <div className="info-card">
          <div className="info-left">
            <div className="info-icon">📍</div>
            <div className="info-label">{t('storeInfo.location')}</div>
          </div>
          <div className="info-value">{storeInfo.Location}</div>
        </div>
        
        <div className="info-card">
          <div className="info-left">
            <div className="info-icon">📏</div>
            <div className="info-label">{t('storeInfo.storeSize')}</div>
          </div>
          <div className="info-value">{storeInfo.store_size ? `${formatNumber(storeInfo.store_size)} ${t('storeInfo.sqftUnit')}` : t('storeInfo.notAvailable')}</div>
        </div>
      </div>
    </div>
  );
};

export default StoreInfoCard;